import type {jobsPayload, AgencyList} from 'src/types/referrer-portal';
import type {ReferralPrograms, ReferralJob} from 'src/types/referral-v2';
import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useSelector, useDispatch} from 'react-redux';
import {useAsyncDependency} from 'src/hooks/useAsyncDependency';
import {isReferrerPortal} from 'src/components/referral-v2/common';
import {selectUserAgent} from 'src/selectors/accounts';
import {isMobile} from 'src/utils/responsive';
import {
  getAgencyPrograms,
  fetchJobDescription,
  getProgramQuestions,
  clearReferralProgramsData,
} from 'src/action-creators/referrer-portal/user-form';
import {
  selectAgencyList,
  selectAgencyReferralProgram,
} from 'src/selectors/referrer-portal';
import {
  getReferrerPortalAssignJobHeader,
  getDefaultAssignJobHeader,
} from 'src/utils/referrer-portal';
import {
  Panel,
  PanelHeader,
  PanelBody,
  PanelFooter,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Card,
  CardHeader,
  CardContent,
  Dropdown,
  SubTitleLarge,
} from '@spaced-out/ui-design-system/lib/components/index';
import JobList from 'src/components/referrer-portal/user-form/job-list';
import JobDetails from 'src/components/referrer-portal/user-form/job-details';
import css from 'src/components/referrer-portal/user-form/jobs.css';


const jobStepper = {
  agencySelect: 0,
  jobSelect: 1,
} as const;

const AssignJobPanel = ({
  isOpen,
  onClose,
  handleAssignJob,
}: {
  isOpen: boolean;
  onClose: () => unknown;
  handleAssignJob: (data: jobsPayload) => unknown;
}) => {
  const dispatch = useDispatch();
  const {t} = useLabelTranslation();
  const REFERRER_PORTAL_ASSIGN_JOB_HEADER = getReferrerPortalAssignJobHeader(t);
  const DEFAULT_ASSIGN_JOB_HEADER = getDefaultAssignJobHeader(t);
  const userAgent = useSelector(selectUserAgent);
  const panelSize = isMobile(userAgent) ? 'small' : 'large';

  const [currentStep, setCurrentStep] = React.useState(jobStepper.agencySelect);
  const [agencyId, setAgencyId] = React.useState('');
  const [programId, setProgramId] = React.useState('');
  const [jobDetailsOpen, setJobDetailsOpen] = React.useState(false);
  const [clickedJobId, setClickedJobId] = React.useState('');

  const isCurrentStepAgencySelect = currentStep === jobStepper.agencySelect;
  const isReferrerPortalEnv = isReferrerPortal();

  const agencyList: AgencyList[] | null | undefined =
    useSelector(selectAgencyList);

  const programList: ReferralPrograms[] | null | undefined = useSelector(
    selectAgencyReferralProgram,
  );

  const agencyFilterOptions = agencyList?.map((agency: AgencyList) => ({
    key: agency.id.toString(),
    label: agency.name,
  }));

  const programFilterOptions = programList?.map(
    (program: ReferralPrograms) => ({
      key: program.id.toString(),
      label: program.name,
    }),
  );

  const selectedAgency = agencyList?.find(
    (agency: AgencyList) => agency.id === agencyId,
  );
  const selectedProgram = programList?.find(
    (program: ReferralPrograms) => program.id.toString() === programId,
  );

  const isActionButtonDisabled = isCurrentStepAgencySelect && !programId;

  const {agencyStepLabel, agencyStepContent, agencyStepCardHeader} =
    isReferrerPortalEnv
      ? REFERRER_PORTAL_ASSIGN_JOB_HEADER
      : DEFAULT_ASSIGN_JOB_HEADER;

  const handleBack = () => setCurrentStep(jobStepper.agencySelect);

  const handleClosePanel = () => {
    setAgencyId('');
    setProgramId('');
    setCurrentStep(jobStepper.agencySelect);
    if (isReferrerPortalEnv) {
      dispatch(clearReferralProgramsData());
    }
    onClose();
  };

  const handleNext = () => {
    if (!isActionButtonDisabled) {
      // @ts-expect-error - TS2345 - Argument of type '1' is not assignable to parameter of type 'SetStateAction<0>'.
      setCurrentStep(jobStepper.jobSelect);
    }
  };

  const onSelectJob = (job: ReferralJob) => {
    const payload = {
      agency_id: agencyId,
      agency_name: selectedAgency?.name || '',
      program_name: selectedProgram?.name || '',
      program_id: programId,
      job_id: job.id,
      job_title: job.title,
    } as const;
    handleClosePanel();
    handleAssignJob(payload);
  };

  const onClickJob = (id: string) => setClickedJobId(id);
  const showJobDescription = () => setJobDetailsOpen(true);

  const isDescriptionLoading = useAsyncDependency(() => {
    const apis: Array<any> = [];
    if (clickedJobId) {
      apis.push(dispatch(fetchJobDescription(agencyId, clickedJobId)));
    }
    return Promise.all(apis);
  }, [clickedJobId]);

  React.useEffect(() => {
    if (agencyId || !isReferrerPortalEnv) {
      dispatch(getAgencyPrograms(agencyId));
    }
  }, [agencyId]);

  React.useEffect(() => {
    const defaultProgram = programList?.find(
      (program: ReferralPrograms) => program.is_default_program,
    );
    setProgramId(defaultProgram?.id.toString() || '');
  }, [programList]);

  React.useEffect(() => {
    if ((agencyId || !isReferrerPortalEnv) && programId) {
      dispatch(getProgramQuestions(agencyId, programId));
    }
  }, [programId]);

  return (
    <>
      <Panel
        size={panelSize}
        anchor="right"
        isOpen={isOpen}
        onClose={handleClosePanel}
      >
        <PanelHeader onCloseButtonClick={handleClosePanel}>
          {t('Select_Job', 'Select Job')}
        </PanelHeader>
        <PanelBody className={css.flexColumn}>
          <Stepper
            activeStep={currentStep}
            classNames={{wrapper: css.footerRightAction}}
          >
            <Step
              index={jobStepper.agencySelect}
              completed={!isCurrentStepAgencySelect}
              onClick={() => setCurrentStep(jobStepper.agencySelect)}
            >
              <StepLabel>{agencyStepLabel}</StepLabel>
              <StepContent>{agencyStepContent}</StepContent>
            </Step>
            <Step index={jobStepper.jobSelect}>
              <StepLabel>{t('Select_Job', 'Select Job')}</StepLabel>
              <StepContent>
                {t(
                  'Choose_a_job_that_aligns_well_with_your_profile',
                  'Choose a job that aligns well with your profile',
                )}
              </StepContent>
            </Step>
          </Stepper>
          {isCurrentStepAgencySelect ? (
            <Card classNames={{wrapper: css.filterCard}}>
              <CardHeader>{agencyStepCardHeader}</CardHeader>
              <CardContent className={css.flexColumn}>
                {isReferrerPortalEnv && (
                  <Dropdown
                    label={t('Select_Organisation', 'Select Organisation')}
                    placeholder={t(
                      'Select_Organisation',
                      'Select Organisation',
                    )}
                    menu={{
                      options: agencyFilterOptions,
                      selectedKeys: [agencyId],
                    }}
                    // @ts-expect-error - TS2339 - Property 'key' does not exist on type 'ChangeEvent<HTMLInputElement> | MenuOption'.
                    onChange={(agency) => setAgencyId(agency.key)}
                    dropdownInputText={selectedAgency?.name}
                  />
                )}
                <Dropdown
                  label={t(
                    'Select_Referral_Program',
                    'Select Referral Program',
                  )}
                  placeholder={t(
                    'Select_Referral_Program',
                    'Select Referral Program',
                  )}
                  menu={{
                    options: programFilterOptions,
                    selectedKeys: [programId],
                  }}
                  disabled={isReferrerPortalEnv && !agencyId}
                  // @ts-expect-error - TS2339 - Property 'key' does not exist on type 'ChangeEvent<HTMLInputElement> | MenuOption'.
                  onChange={(program) => setProgramId(program.key)}
                  dropdownInputText={selectedProgram?.name}
                  classNames={{wrapper: css.filterCard}}
                />
              </CardContent>
            </Card>
          ) : (
            <div className={css.filterCard}>
              <JobList
                agencyId={agencyId}
                programId={programId}
                handleAssignJob={onSelectJob}
                setClickedJobId={onClickJob}
                setJobDetailsOpen={showJobDescription}
              />
            </div>
          )}
        </PanelBody>
        <PanelFooter>
          {isCurrentStepAgencySelect ? (
            <div className={css.footerRightAction}>
              <Button type="tertiary" onClick={handleClosePanel}>
                {t('Cancel', 'Cancel')}
              </Button>
              <Button
                disabled={isActionButtonDisabled}
                iconRightName={
                  isCurrentStepAgencySelect ? 'chevron-right' : 'check'
                }
                onClick={handleNext}
              >
                {isCurrentStepAgencySelect
                  ? t('Next', 'Next')
                  : t('Save', 'Save')}
              </Button>
            </div>
          ) : (
            <Button
              iconLeftName="chevron-left"
              type="secondary"
              onClick={handleBack}
            >
              {t('Back', 'Back')}
            </Button>
          )}
        </PanelFooter>
      </Panel>
      <Panel
        size={panelSize}
        anchor="right"
        onClose={() => {
          setJobDetailsOpen(false);
        }}
        isOpen={!!clickedJobId && jobDetailsOpen}
      >
        <PanelHeader onCloseButtonClick={() => setJobDetailsOpen(false)}>
          <div>
            <Button
              type="ghost"
              onClick={() => {
                setJobDetailsOpen(false);
              }}
              iconLeftName="chevron-left"
              classNames={{wrapper: css.button}}
            >
              <SubTitleLarge>{t('Job_Details', 'Job Details')}</SubTitleLarge>
            </Button>
          </div>
        </PanelHeader>
        <JobDetails
          setJobDetailsOpen={setJobDetailsOpen}
          handleSelectJob={onSelectJob}
          jobId={clickedJobId ?? ''}
          isDataLoading={isDescriptionLoading}
        />
      </Panel>
    </>
  );
};
export default AssignJobPanel;
